import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { setDisplayTzPrefs } from "../store/slices/userPreferencesSlice";
import { resolveDisplayTz } from "../common/utilities/displayTime";
import { systemDisplayTimezone } from "../common/constants/displayTimezones";
import { httpClient } from "../lib/http-client";
import { logger } from "../services/logger.service";

/**
 * The IANA zone to RENDER a time in, for the current viewer. Personal-only: the user's
 * personal display tz, else the Pacific floor. Used uniformly for every instant (tours,
 * audit timestamps, Overview aggregates, the whole app).
 *
 * Usage: formatInstant(iso, useDisplayTz())
 *
 * OPT-IN community override (global community filter, SALES tz-YES surfaces only): pass the
 * selected community's zone via useCommunityTz() -> useDisplayTz(useCommunityTz()). When a
 * community is selected its zone wins; All/null/invalid falls through to the personal chain.
 * The NO-ARG form is byte-identical to before, so tz-NO surfaces are untouched by construction.
 */
export function useDisplayTz(communityTz?: string | null): string {
  const userTz = useSelector((s: RootState) => s.userPreferencesReducer.userTimezone);
  // instanceTz (email_digest_config) is intentionally NOT consulted for display: an unset
  // user floors straight to Pacific. The slice still carries it for the notification panel.
  return resolveDisplayTz(userTz, communityTz);
}

/**
 * One-time-per-instance loader: pulls the user's display-tz prefs (personal tz)
 * and the instance fallback from the single GET /api/v1/user-preferences call
 * into the userPreferences slice, so every useDisplayTz reads them without re-fetching.
 * Mounted app-wide (AppLayout) so both the Sales + Marketing toggles resolve the same.
 */
export function useLoadDisplayTzPrefs(instanceId: string | undefined): void {
  const dispatch = useDispatch();
  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    (async () => {
      try {
        // Source = the APP-WIDE per-user pref (public.user_preferences), reachable on both
        // the Sales + Marketing toggles — NOT the CRM digest field. Returns the personal tz +
        // the resolved instance fallback (email_digest_config) in one call.
        const res = await httpClient.get<{
          timezone?: string | null;
          timezoneMode?: "personal" | "community";
          instanceTimezone?: string | null;
        }>(`/api/v1/user-preferences?instanceId=${instanceId}`);
        if (cancelled) return;

        // Option-B AUTO-DEFAULT: a user with NO personal tz gets their normalized system zone
        // written ONCE, automatically. Uses the value ALREADY fetched (no extra read). The write
        // is a server-side conditional upsert (WHERE timezone IS NULL) -> idempotent, race-safe,
        // never overwrites a manual choice, one write per user ever (after set, GET returns it so
        // this branch never fires again). normalize-or-skip: systemDisplayTimezone() is "" for an
        // unrecognized zone -> we write nothing and the chain floors to Pacific. This only
        // populates the STORED value once; it is NOT a per-render browser-follow (the resolution
        // chain stays user_preferences -> Pacific).
        let userTimezone = res.data?.timezone ?? null;
        if (!userTimezone) {
          const sysZone = systemDisplayTimezone();
          if (sysZone) {
            try {
              const w = await httpClient.put<{ set?: boolean; timezone?: string | null }>(
                "/api/v1/user-preferences/default-timezone",
                { timezone: sysZone },
              );
              if (!cancelled && w.data?.set && w.data.timezone) userTimezone = w.data.timezone;
            } catch (e) {
              // non-fatal: leave unset -> the chain floors to Pacific. Log it (the outer catch
              // logs load failures; this inner write-failure was previously silent).
              logger.warn("[useDisplayTz] auto-default timezone write failed (non-fatal)", {
                error: e,
              });
            }
            if (cancelled) return;
          }
        }

        dispatch(
          setDisplayTzPrefs({
            userTimezone,
            userTimezoneMode: res.data?.timezoneMode ?? "personal",
            instanceTimezone: res.data?.instanceTimezone ?? null,
          }),
        );
      } catch (err) {
        // Non-fatal: the chain stays total (falls to instance/floor) if prefs don't load.
        logger.error("[useDisplayTz] failed to load display-tz prefs", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instanceId, dispatch]);
}
